/**
 * Sprint 30 M5 — Library adoption telemetry.
 *
 * Single server fn that captures policy-library interaction events to
 * `app_logs` (source = `policy_library`). No new tables, no dashboards —
 * future reporting derives from `app_logs.extras`.
 *
 * Events (event_name):
 *   library.view                — Library / lens opened
 *   library.search              — Search query executed (with hasResults)
 *   library.search.empty        — Search returned zero results
 *   library.saved_query         — Saved-query chip clicked
 *   library.facet               — Facet (capability/status) used
 *   library.row.open            — Row click → detail
 *   policy.detail.view          — Detail page opened (with prior_detail)
 *   policy.detail.dwell         — Detail page time-on-page on unmount (ms)
 *   create_policy.opened        — Global / lens "Create Policy" affordance
 *   propose.proposal_generated  — AI returned a proposal
 *   propose.activated           — Operator activated a proposal
 *   propose.abandoned           — Sheet closed without activation
 *   advanced.opened             — Legacy editor opened
 *   advanced.saved              — Legacy editor save succeeded
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const Input = z.object({
  tenantId: z.string().uuid().nullable().optional(),
  event: z.string().min(1).max(64),
  source: z.string().min(1).max(64).optional(), // e.g. "global", "hold_creation"
  sessionId: z.string().min(1).max(64).optional(),
  props: z.record(z.string(), z.any()).optional(),
});

export const logPolicyEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => Input.parse(i))
  .handler(async ({ data, context }) => {
    // Best-effort; telemetry failures must never surface to the user.
    try {
      await (context.supabase as any).from("app_logs").insert({
        level: "info",
        source: "policy_library",
        message: data.event,
        user_id: context.userId,
        tenant_id: data.tenantId ?? null,
        extras: {
          event: data.event,
          surface: data.source ?? "global",
          session_id: data.sessionId ?? null,
          ...(data.props ?? {}),
        },
      });
    } catch {
      // swallow — telemetry must be fire-and-forget
    }
    return { ok: true };
  });